/**
 * Planner (spec page 7, approved - simple): Baker's own work only.
 * Today / Tomorrow / This week lists + a tray of unplanned open requests in
 * lineup order. Drag between them. Each list shows hours vs capacity. Check an
 * item off by logging hours (it leaves the planner; the request itself keeps
 * its stage). Unfinished Today items roll forward; Tomorrow becomes Today when
 * the date arrives (lib/order.effectiveSlot).
 */
import { useState } from 'react';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import type { PlannerSlot, Settings } from '../config';
import { logWork, setPlanner, type Me, type Req } from '../lib/data';
import { effectiveSlot, lineupOrder } from '../lib/order';
import { StatusTag, TierBadge } from '../ui';
import { Guide } from '../guide';

const LISTS: { slot: PlannerSlot; label: string; cap: (s: Settings) => number }[] = [
  { slot: 'today', label: 'Today', cap: (s) => s.capacityDay },
  { slot: 'tomorrow', label: 'Tomorrow', cap: (s) => s.capacityDay },
  { slot: 'week', label: 'This week', cap: (s) => s.capacityWeek },
];

export function Planner({ me, items, settings, go }: { me: Me; items: Req[]; settings: Settings; go: (to: string) => void }) {
  const [err, setErr] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const open = lineupOrder(items.filter((r) => r.status !== 'completed'));
  const slotOf = (r: Req) => effectiveSlot(r.plannerSlot, r.plannerDate);
  const inSlot = (slot: PlannerSlot) => open.filter((r) => slotOf(r).slot === slot);
  const tray = open.filter((r) => slotOf(r).slot === 'none' && r.status !== 'new');
  const untriaged = open.filter((r) => slotOf(r).slot === 'none' && r.status === 'new').length;

  async function onDragEnd(e: DragEndEvent) {
    const r = items.find((x) => x.id === e.active.id);
    const to = e.over?.id as PlannerSlot | undefined;
    if (!r || !to || to === slotOf(r).slot) return;
    setErr(null);
    try { await setPlanner(me, r, to); } catch (x) { setErr((x as Error).message); }
  }

  return (
    <div>
      <div className="toolbar">
        <h2 className="page-title">Planner</h2>
        <span className="muted small">Drag from the tray into a day. Log hours to check something off.</span>
      </div>
      <Guide page="planner" />
      {err && <p className="err">{err}</p>}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="planner">
          {LISTS.map((l) => {
            const list = inSlot(l.slot);
            const hours = list.reduce((s, r) => s + (r.estHours ?? 0), 0);
            const cap = l.cap(settings);
            return (
              <Drop key={l.slot} id={l.slot} className="plan-col">
                <div className="col-head"><span>{l.label}</span><span className={`count ${hours > cap ? 'over-cap' : ''}`}>{hours}h / {cap}h</span></div>
                <div className="capbar"><span style={{ width: `${Math.min(100, (hours / cap) * 100)}%` }} className={hours > cap ? 'over-cap' : ''} /></div>
                {!list.length && <p className="empty">Drop work here.</p>}
                {list.map((r) => (
                  <Drag key={r.id} id={r.id}>
                    <PlanItem r={r} settings={settings} rolled={slotOf(r).rolled} canLog={l.slot === 'today'}
                      onOpen={() => go(`/r/${r.id}`)} onLog={async (h, keep) => {
                        setErr(null);
                        try { await logWork(me, r, h, keep); } catch (x) { setErr((x as Error).message); }
                      }} />
                  </Drag>
                ))}
              </Drop>
            );
          })}
          <Drop id="none" className="plan-col tray">
            <div className="col-head"><span>Tray</span><span className="count">{tray.length}</span></div>
            <p className="muted small">Unplanned, in lineup order.{untriaged ? ` ${untriaged} New request${untriaged > 1 ? 's' : ''} still need triage first.` : ''}</p>
            {tray.map((r) => (
              <Drag key={r.id} id={r.id}><PlanItem r={r} settings={settings} rolled={false} canLog={false} onOpen={() => go(`/r/${r.id}`)} onLog={async () => undefined} /></Drag>
            ))}
          </Drop>
        </div>
      </DndContext>
    </div>
  );
}

function PlanItem({ r, settings, rolled, canLog, onOpen, onLog }: {
  r: Req; settings: Settings; rolled: boolean; canLog: boolean; onOpen: () => void; onLog: (hours: number, keep: boolean) => Promise<void>;
}) {
  const [logging, setLogging] = useState(false);
  const [hours, setHours] = useState(String(r.estHours ?? 1));
  const submit = async (keep: boolean) => {
    const h = Number(hours);
    if (Number.isNaN(h) || h < 0) return;
    await onLog(h, keep);
    setLogging(false);
  };
  return (
    <div className="plan-item">
      <div className="pi-top">
        {canLog && <button className="check" title="Log hours / check off" onPointerDown={(e) => e.stopPropagation()} onClick={() => setLogging((v) => !v)} />}
        <span className="pi-title" onClick={onOpen}>{r.title}</span>
        <span className="num">{r.estHours != null ? `${r.estHours}h` : ''}</span>
      </div>
      <div className="pi-meta">
        <StatusTag status={r.status} /> <TierBadge r={r} settings={settings} />
        {rolled && <span className="rolled">rolled over</span>}
        {r.waitingOn && <span className="waiting">waiting: {r.waitingOn}</span>}
      </div>
      {logging && (
        <div className="pi-log" onPointerDown={(e) => e.stopPropagation()}>
          <input className="num-input" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} autoFocus />
          <span className="muted small">hours today</span>
          <button className="secondary" onClick={() => submit(true)}>Log + keep</button>
          <button onClick={() => submit(false)}>Log + clear</button>
        </div>
      )}
    </div>
  );
}

function Drop({ id, className, children }: { id: string; className: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`${className} ${isOver ? 'over' : ''}`}>{children}</div>;
}
function Drag({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  return <div ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''} {...listeners} {...attributes}>{children}</div>;
}
